import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {Navigate, Route, Routes, NavLink, Outlet, useLocation, useNavigate} from 'react-router-dom';
import styles from './DataHub.module.css';

import DataUpload from './Upload';
import UploadDashboard from './UploadDashboard/UploadDashboard';
import DataQAReview from './DataQAReview/DataQAReview';
import DataReviewHub from './ReviewHome/DataReviewHub';
import FleetV2 from './Fleet/FleetV2';
import PartnerDevice from './PartnerDevice/PartnerDevice';
import ReviewUserProgress from './ReviewHome/ReviewUserProgress';

import {SwapMainAppContext} from '../../context/SwapMainAppContext';
import {PRISMAX_DATA_PIPELINE_URL} from '../../configs/config';
// import RegisterRobotModal from '../Account/OperatorSection/RegisterRobotModal';
import {getApiErrorMessage, isSessionExpiredError} from '../../utils/authErrors';
import {resolveQaRole} from '../../utils/userRoles';

import uploadDataIcon from '../../static_files/upload_data.svg';
import verifyIcon from '../../static_files/verify.svg';
// import registerIcon from '../../static_files/register.svg';
import folderIcon from '../../static_files/folder.svg';
import arrowRightIcon from '../../static_files/ep_arrow-right-bold.svg';


let taskListCache = null;
let machineListCache = null;
let taskListPromise = null;
let machineListPromise = null;

const fetchTaskListOnce = async () => {
    if (Array.isArray(taskListCache)) return taskListCache;
    if (!taskListPromise) {
        taskListPromise = fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/tasks`)
            .then((res) => {
                if (!res.ok) throw new Error('Failed to load tasks');
                return res.json();
            })
            .then((json) => {
                taskListCache = json?.data || [];
                return taskListCache;
            })
            .finally(() => {
                taskListPromise = null;
            });
    }
    return taskListPromise;
};

const fetchMachineListOnce = async (token) => {
    if (Array.isArray(machineListCache)) return machineListCache;
    if (!machineListPromise) {
        machineListPromise = fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/registered-machines`, {
            headers: { 
                Authorization: `Bearer ${token}`
            }
        })
            .then(async (res) => {
                if (!res.ok) {
                    const err = await res.json().catch(() => ({}));
                    throw new Error(getApiErrorMessage(err, 'Failed to load machines'));
                }
                return res.json();
            })
            .then((json) => {
                machineListCache = json?.data || [];
                return machineListCache;
            })
            .finally(() => {
                machineListPromise = null;
            });
    }
    return machineListPromise;
};

const getTaskCategory = (task) => {
    const environment = String(task?.environment || '').trim();
    if (environment) return environment;
    const scenario = String(task?.scenario || '').trim();
    if (scenario) return scenario;
    return String(task?.scene || '').trim();
};

const formatUtcDate = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC'});
};

// The reinstate-due scheduler only runs once every 24 hours, so a user whose
// desired_reinstated_at falls mid-day isn't actually restored until the next
// run. Round up to the next UTC calendar day so the displayed date matches
// reality instead of promising same-day reinstatement.
const ceilToNextUtcDayIfNeeded = (iso) => {
    if (!iso) return null;
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    const hasTimeComponent = date.getUTCHours() !== 0 || date.getUTCMinutes() !== 0
        || date.getUTCSeconds() !== 0 || date.getUTCMilliseconds() !== 0;
    const dayStart = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
    return new Date(hasTimeComponent ? dayStart + 24 * 60 * 60 * 1000 : dayStart);
};

const QaBlockBanner = ({ qaBlockStatus }) => {
    if (!qaBlockStatus?.blocked) return null;
    const blockedAtDisplay = qaBlockStatus.blockedAt ? formatUtcDate(new Date(qaBlockStatus.blockedAt)) : null;
    const reinstateDisplay = formatUtcDate(ceilToNextUtcDayIfNeeded(qaBlockStatus.desiredReinstatedAt));
    return (
        <div className={styles.qaBlockBanner}>
            <span className={styles.qaBlockBannerTitle}>
                Your QA review access is temporarily paused due to abnormal review activity.
            </span>
            <span className={styles.qaBlockBannerBody}>
                {blockedAtDisplay && reinstateDisplay
                    ? ` It runs from ${blockedAtDisplay} to ${reinstateDisplay} UTC, and only affects the QA review section — the rest of your account is unaffected.`
                    : ' It only affects the QA review section — the rest of your account is unaffected.'}
            </span>
        </div>
    );
};

const DataReviewHubWrapper = ({ dataTasks, qaUploads, qaUploadsStatus }) => {
    if (qaUploadsStatus === 'error') {
        return (
            <div className={styles.placeholder}>
                <h2>Review Datasets</h2>
                <p>Failed to load uploads.</p>
            </div>
        );
    }
    return (
        <DataReviewHub
            dataTasks={dataTasks}
            qaUploads={qaUploads}
            isQaUploadsLoading={qaUploadsStatus === 'loading' || qaUploadsStatus === 'idle'}
        />
    );
};


const ReviewWorkspace = ({ isReviewViewerRoute, canAccess, isLoggedIn, qaBlockStatus }) => {
    const { showNotification } = useContext(SwapMainAppContext);
    const noAccessSection = (
        <div className={styles.placeholder}>
            <h2>Review Datasets</h2>
            {
                !isLoggedIn ? <p>Please log in to access Review Datasets.</p>
                : <p>Unable to access Review Datasets</p>
            }
        </div>
    )

    const handleProgressRestrictedClick = (event) => {
        event.preventDefault();
        showNotification?.('warning', 'My Progress is unavailable while QA review is paused.');
    };

    // Hide the top subnavigation bar for the review viewer route only
    if (isReviewViewerRoute) {
        return (
            <>
                <QaBlockBanner qaBlockStatus={qaBlockStatus} />
                { canAccess ? <Outlet /> : noAccessSection }
            </>
        );
    }

    return (
        <>
            <QaBlockBanner qaBlockStatus={qaBlockStatus} />
            <div className={styles.dataHubPageSubnav}>
                <NavLink
                    to="/data/review"
                    end
                    className={({isActive}) => (
                        isActive
                            ? `${styles.dataHubPageSubnavLink} ${styles.dataHubPageSubnavLinkActive}`
                            : styles.dataHubPageSubnavLink
                    )}
                >
                    Review & Earn
                </NavLink>
                <NavLink
                    to="/data/review/progress"
                    onClick={qaBlockStatus?.blocked ? handleProgressRestrictedClick : undefined}
                    className={({isActive}) => {
                        if (qaBlockStatus?.blocked) return `${styles.dataHubPageSubnavLink} ${styles.dataHubPageSubnavLinkDisabled}`;
                        return isActive
                            ? `${styles.dataHubPageSubnavLink} ${styles.dataHubPageSubnavLinkActive}`
                            : styles.dataHubPageSubnavLink;
                    }}
                >
                    My Progress
                </NavLink>
            </div>
            <div className={styles.uploadWorkspaceBody}>
                { canAccess ? <Outlet /> : noAccessSection }
            </div>
        </>
       
    )
}


const DataReviewViewer = ({ qaUploads, setQaUploads, qaUploadsStatus, dataTasks }) => {
    if (qaUploadsStatus === 'error') {
        return (
            <div className={styles.placeholder}>
                <h2>Review Datasets</h2>
                <p>Failed to load uploads.</p>
            </div>
        );
    }
    if (qaUploadsStatus !== 'ready') {
        return (
            <div className={styles.placeholder}>
                <h2>Review Datasets</h2>
                <p>Loading uploads...</p>
            </div>
        );
    }
    return <DataQAReview qaUploads={qaUploads} setQaUploads={setQaUploads} dataTasks={dataTasks} skipUploadsFetch />;
};

const UploadWorkspace = ({ canAccess, scenarioOptions = [], selectedScenario, onScenarioSelect }) => {
    const location = useLocation();
    const isDashboardRoute = location.pathname.startsWith('/data/upload/dashboard');

    return (
        <div className={styles.uploadWorkspace}>
            <div className={styles.dataHubPageSubnav}>
                <NavLink
                    to="/data/upload"
                    end
                    className={({isActive}) => (
                        isActive
                            ? `${styles.dataHubPageSubnavLink} ${styles.dataHubPageSubnavLinkActive}`
                            : styles.dataHubPageSubnavLink
                    )}
                >
                    Upload
                </NavLink>
                <NavLink
                    to="/data/upload/dashboard"
                    className={({isActive}) => (
                        isActive
                            ? `${styles.dataHubPageSubnavLink} ${styles.dataHubPageSubnavLinkActive}`
                            : styles.dataHubPageSubnavLink
                    )}
                >
                    Dashboard
                </NavLink>
            </div>

            {!isDashboardRoute && scenarioOptions.length > 0 && (
                <div className={styles.mobileScenarioBar}>
                    {scenarioOptions.map((scenario) => (
                        <button
                            key={scenario}
                            type="button"
                            onClick={() => onScenarioSelect?.(scenario)}
                            className={selectedScenario === scenario
                                ? `${styles.mobileScenarioChip} ${styles.mobileScenarioChipActive}`
                                : styles.mobileScenarioChip}
                        >
                            <img src={folderIcon} alt="" className={styles.scenarioFolder} />
                            <span>{scenario}</span>
                        </button>
                    ))}
                </div>
            )}

            <div className={styles.uploadWorkspaceBody}>
                {(isDashboardRoute && !canAccess)
                ? (
                    <div className={styles.placeholder}>
                        <h2>Operator Dashboard</h2>
                        <p>This area is restricted to Operator users.</p>
                    </div>
                ) : <Outlet />
                }
            </div>
        </div>
    );
};

const DataHub = () => {
    const {
        userRole,
        qaRole,
        showNotification,
        gatewayToken,
        shouldRefreshVLADataCache,
        setShouldRefreshVLADataCache,
        handleUserSessionExpired
    } = useContext(SwapMainAppContext);
    const normalizedUserRole = String(userRole || '').trim().toLowerCase();
    const isOperator = normalizedUserRole === 'operator';
    const hasQaRole = Boolean(resolveQaRole(qaRole, userRole));
    // const isOperatorBlockedFromQA = isOperator && !hasQaRole; // PRIS-324: have commented this out because operators now have QA access
    
    const location = useLocation();
    const navigate = useNavigate();
    const [dataTasks, setDataTasks] = useState([]);
    const [dataMachines, setDataMachines] = useState([]);
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [selectedScenario, setSelectedScenario] = useState('');
    const [qaUploads, setQaUploads] = useState([]);
    const [qaUploadsStatus, setQaUploadsStatus] = useState('idle');
    // const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
    
    const isLoggedIn = Boolean(gatewayToken || localStorage.getItem('gatewayToken'));
    // const canAccessQAReview = isLoggedIn && !isOperatorBlockedFromQA;
    const canAccessQAReview = isLoggedIn;
    const [qaBlockStatus, setQaBlockStatus] = useState({ blocked: false, blockedAt: null, desiredReinstatedAt: null });

    const isUploadRoute = useMemo(() => {
        return location.pathname === '/data' || location.pathname.startsWith('/data/upload');
    }, [location.pathname]);

    const isReviewViewerRoute = useMemo(() => {
        return location.pathname.startsWith('/data/review/') && location.pathname !== '/data/review/progress';
    }, [location.pathname]);

    const shouldShowNoBackgroundRoute = useMemo(() => {
        return location.pathname.startsWith('/data/fleet') || location.pathname.startsWith('/data/partner-device')
    }, [location.pathname]);

    const loadMachineData = useCallback(async ({force = false, isCancelled = () => false} = {}) => {
        if (!gatewayToken || !isUploadRoute) {
            if (!isCancelled()) {
                setDataMachines([]);
            }
            return;
        }

        if (force) {
            machineListCache = null;
        }

        try {
            const machines = await fetchMachineListOnce(gatewayToken);
            if (!isCancelled()) {
                setDataMachines(machines);
            }
        } catch (error) {
            console.error('Failed to load data machines:', error);
            if (isCancelled()) return;
            if (isSessionExpiredError(error)) {
                handleUserSessionExpired?.();
                return;
            }
            setDataMachines([]);
        }
    }, [gatewayToken, isUploadRoute, handleUserSessionExpired]);

    const loadReferenceData = useCallback(async ({force = false} = {}) => {
        if (!gatewayToken) {
            showNotification('error', 'Missing gateway token. Please log in again.');
            return;
        }
        if (force) {
            taskListCache = null;
        }

        void loadMachineData({force});

        if (!Array.isArray(taskListCache)) {
            setIsDataLoading(true);
        }
        try {
            const tasks = await fetchTaskListOnce();
            setDataTasks(tasks);
        } catch (error) {
            console.error('Failed to load data tasks:', error);
            if (isSessionExpiredError(error)) {
                handleUserSessionExpired?.();
                return;
            }
            setDataTasks([]);
        } finally {
            setIsDataLoading(false);
        }
    }, [gatewayToken, showNotification, handleUserSessionExpired, loadMachineData]);

    useEffect(() => {
        if (!gatewayToken) {
            setDataTasks([]);
            setDataMachines([]);
            setIsDataLoading(false);
            return;
        }

        let cancelled = false;
        const bootstrap = async () => {
            try {
                // Refresh the VLA cache on mount in case another component updated the data
                if (shouldRefreshVLADataCache) {
                    taskListCache = null;
                    setShouldRefreshVLADataCache(false);
                }

                void loadMachineData({
                    force: shouldRefreshVLADataCache,
                    isCancelled: () => cancelled,
                });

                if (!Array.isArray(taskListCache)) {
                    setIsDataLoading(true);
                }
                const tasks = await fetchTaskListOnce();
                if (cancelled) return;
                setDataTasks(tasks);
            } catch (error) {
                console.error('Failed to load data tasks:', error);
                if (cancelled) return;
                if (isSessionExpiredError(error)) {
                    handleUserSessionExpired?.();
                    return;
                }
                setDataTasks([]);
            } finally {
                if (!cancelled) {
                    setIsDataLoading(false);
                }
            }
        };

        void bootstrap();
        return () => {
            cancelled = true;
        };
    }, [gatewayToken, setShouldRefreshVLADataCache, shouldRefreshVLADataCache, handleUserSessionExpired, loadMachineData]);

    const scenarioOptions = useMemo(() => {
        const seen = new Set();
        const categories = [];
        dataTasks.forEach((task) => {
            const category = getTaskCategory(task);
            const key = category.toLowerCase();
            if (!category || seen.has(key)) return;
            seen.add(key);
            categories.push(category);
        });
        return categories;
    }, [dataTasks]);

    useEffect(() => {
        if (!scenarioOptions.length) {
            setSelectedScenario('');
            return;
        }
        setSelectedScenario((prev) => (
            prev && scenarioOptions.includes(prev) ? prev : scenarioOptions[0]
        ));
    }, [scenarioOptions]);

    const ensureQaUploads = useCallback(async () => {
        if (!canAccessQAReview) return;
        if (qaUploadsStatus !== 'idle') return;
        const token = gatewayToken || localStorage.getItem('gatewayToken');
        if (!token) {
            setQaUploadsStatus('error');
            if (typeof showNotification === 'function') {
                showNotification('error', 'Missing gateway token. Please log in again.');
            }
            return;
        }
        try {
            setQaUploadsStatus('loading');
            const res = await fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/qa/uploads`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(getApiErrorMessage(err, 'Failed to load QA uploads'));
            }
            const json = await res.json();
            setQaUploads(json?.data || []);
            setQaUploadsStatus('ready');
        } catch (error) {
            console.error('Failed to load QA uploads:', error);
            setQaUploadsStatus('error');
            if (isSessionExpiredError(error)) {
                handleUserSessionExpired?.();
                return;
            }
            if (typeof showNotification === 'function') {
                showNotification('error', error.message || 'Failed to load QA uploads.');
            }
        }
    }, [canAccessQAReview, qaUploadsStatus, gatewayToken, showNotification, handleUserSessionExpired]);

    useEffect(() => {
        void ensureQaUploads();
    }, [ensureQaUploads]);

    useEffect(() => {
        if (!isLoggedIn) {
            setQaBlockStatus({ blocked: false, blockedAt: null, desiredReinstatedAt: null });
            return;
        }
        const token = gatewayToken || localStorage.getItem('gatewayToken');
        if (!token) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/qa/blocklist/my-status`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const json = await res.json().catch(() => ({}));
                if (cancelled) return;
                if (res.ok && json.success && json.data) {
                    setQaBlockStatus({
                        blocked: Boolean(json.data.blocked),
                        blockedAt: json.data.blocked_at || null,
                        desiredReinstatedAt: json.data.desired_reinstated_at || null,
                    });
                }
            } catch (error) {
                console.error('Failed to load QA blocklist status:', error);
            }
        })();
        return () => { cancelled = true; };
    }, [isLoggedIn, gatewayToken]);

    const handleQAReviewRestrictedClick = (event) => {
        event.preventDefault();
        if (!isLoggedIn) {
            showNotification('warning', 'Please log in first to access Review Datasets');
        } 
        // else if (isOperatorBlockedFromQA) {
        //     showNotification('warning', 'Operators are not able to access Review Datasets.');
        // }
        else {
            showNotification('warning', 'Unable to access Review Datasets.');
        }
    };

    // const handleRegisterNewRobot = () => {
    //     if (!gatewayToken) {
    //         showNotification('warning', 'Please log in first to register a new robot');
    //         return;
    //     }
    //     setIsRegisterModalOpen(true);
    // };

    const handleScenarioSelect = useCallback((scenario) => {
        setSelectedScenario(scenario);
        if (location.pathname.startsWith('/data/upload/history')) {
            navigate('/data/upload');
        }
    }, [location.pathname, navigate]);

    return (
        <>
        <div className={styles.layout}>

            <div className={`${styles.content} ${isReviewViewerRoute ? styles.contentNoSidebar : ''}`}>
                {!isReviewViewerRoute && (
                <aside className={styles.sidebar}>
                    <div className={styles.sidebarBlock}>
                        <NavLink
                            to="upload"
                            className={({isActive}) => isActive ? `${styles.navItem} ${styles.navItemActive}` : styles.navItem}
                        >
                            <img src={uploadDataIcon} alt="" className={styles.navIcon} />
                            <span>Upload Data</span>
                        </NavLink>

                        {isUploadRoute && (
                            <div className={styles.scenarioList}>
                                {scenarioOptions.map((scenario) => (
                                    <button
                                        key={scenario}
                                        type="button"
                                        onClick={() => handleScenarioSelect(scenario)}
                                        className={selectedScenario === scenario
                                            ? `${styles.scenarioItem} ${styles.scenarioItemActive}`
                                            : styles.scenarioItem}
                                    >
                                        <img src={arrowRightIcon} alt="" className={styles.scenarioArrow} />
                                        <img src={folderIcon} alt="" className={styles.scenarioFolder} />
                                        <span>{scenario}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className={styles.sidebarBlock}>
                        <NavLink
                            to="review"
                            onClick={canAccessQAReview ? undefined : handleQAReviewRestrictedClick}
                            className={({isActive}) => {
                                // if (isOperatorBlockedFromQA) return `${styles.navItem} ${styles.navItemDisabled}`;
                                return isActive ? `${styles.navItem} ${styles.navItemActive}` : styles.navItem;
                            }}
                        >
                            <img src={verifyIcon} alt="" className={styles.navIcon} />
                            <span>Verify Quality</span>
                        </NavLink>

                        {/* <button
                            type="button"
                            className={`${styles.navItem}`}
                            onClick={handleRegisterNewRobot}
                        >
                            <img src={registerIcon} alt="" className={styles.navIcon} />
                            <span>Register Robots</span>
                        </button> */}
                    </div>

                    <div className={styles.sidebarDivider} />

                    <div className={styles.sidebarBlock}>
                        <div className={styles.navSectionLabel}>Device Partners</div>
                        <NavLink
                            to="fleet"
                            className={({isActive}) => isActive ? `${styles.navItem} ${styles.navItemActive}` : styles.navItem}
                        >
                            <svg className={styles.navIcon} width="15" height="15" viewBox="0 0 15 15" fill="none">
                                <rect x="2.5" y="5" width="10" height="7" rx="1.8" stroke="currentColor" strokeWidth="1.2"/>
                                <circle cx="5.5" cy="8.5" r="0.9" fill="currentColor"/>
                                <circle cx="9.5" cy="8.5" r="0.9" fill="currentColor"/>
                                <path d="M7.5 5V2.5" stroke="currentColor" strokeWidth="1.2"/>
                                <circle cx="7.5" cy="2.2" r="1" fill="currentColor"/>
                            </svg>
                            <span>Robot Fleet</span>
                        </NavLink>
                        <NavLink
                            to="partner-device"
                            className={({isActive}) => isActive ? `${styles.navItem} ${styles.navItemActive}` : styles.navItem}
                        >
                            <svg className={styles.navIcon} width="15" height="15" viewBox="0 0 15 15" fill="none">
                                <circle cx="6" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.2"/>
                                <path d="M2 12.5c0-2.2 1.8-3.5 4-3.5s4 1.3 4 3.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                                <path d="M11.5 5v4M9.5 7h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                            </svg>
                            <span>Become a Partner</span>
                        </NavLink>
                    </div>

                </aside>
                )}

                <main className={`${styles.main} ${isReviewViewerRoute ? styles.mainReviewViewerRoute : ''} ${shouldShowNoBackgroundRoute ? styles.mainNoBackgroundRoute : ''}`}>
                    <Routes>
                        <Route path="/" element={<Navigate to="upload" replace/>} />
                        
                        <Route
                            path="upload"
                            element={(
                                <UploadWorkspace
                                    canAccess={isOperator}
                                    scenarioOptions={scenarioOptions}
                                    selectedScenario={selectedScenario}
                                    onScenarioSelect={handleScenarioSelect}
                                />
                            )}
                        >
                            <Route
                                index
                                element={(
                                     <DataUpload
                                        scenarioFilter={selectedScenario}
                                        dataTasks={dataTasks}
                                        dataMachines={dataMachines}
                                        isDataLoading={isDataLoading}
                                    />
                                )}
                            />
                            <Route
                                path="dashboard"
                                element={(
                                     <UploadDashboard />
                                )}
                            />
                            <Route path="*" element={<Navigate to="/data/upload" replace />} />
                        </Route>

                        <Route
                            path="review"
                            element={(
                                <ReviewWorkspace
                                    isReviewViewerRoute={isReviewViewerRoute}
                                    canAccess={canAccessQAReview}
                                    isLoggedIn={isLoggedIn}
                                    qaBlockStatus={qaBlockStatus}
                                />
                            )}
                        >
                            <Route
                                index
                                element={(
                                    <DataReviewHubWrapper
                                        dataTasks={dataTasks}
                                        qaUploads={qaUploads}
                                        qaUploadsStatus={qaUploadsStatus}
                                    />
                                )}
                            />
                            <Route
                                path="progress"
                                element={qaBlockStatus.blocked ? <Navigate to="/data/review" replace /> : <ReviewUserProgress />}
                            />
                            <Route
                                path=":taskId"
                                element={(
                                    <DataReviewViewer
                                        qaUploads={qaUploads}
                                        setQaUploads={setQaUploads}
                                        dataTasks={dataTasks}
                                        qaUploadsStatus={qaUploadsStatus}
                                    />
                                )}
                            />
                        </Route>

                        <Route
                            path="partner-device"
                            element={(
                                <PartnerDevice />
                            )}
                        />
                        
                        <Route
                            path="fleet"
                            element={<FleetV2 />}
                        />

                    </Routes>
                </main>
            </div>
        </div>
        {/* {isRegisterModalOpen && (
            <RegisterRobotModal
                onClose={() => setIsRegisterModalOpen(false)}
                onRefreshData={() => loadReferenceData({ force: true })}
            />
        )} */}
        </>
    );
};

export default DataHub;
